import React, { useRef } from "react";
import { useGarmentMeasurementServiceContext } from "../../../../contexts/GarmentMeasurementServiceContext";
import MainButton from "../../../button/MainButton";
import { CloudChange, DocumentUpload, Add, Trash, ImportCurve, InfoCircle } from "iconsax-react";
import GarmentMeasurementServicePagination from "../GarmentMeasurementServicePagination";
import UploadErrorMessage from "../../../error/UploadErrorMessage";
import { processImageForDisplay } from "../../../../utilities/helper/processImageForDisplay";

const ImageUpload: React.FC = () => {
  const {
    uploadedImages,
    handleImageUpload,
    handleMultipleImagesUpload,
    imageErrors,
    removeImage,
    addNewImageSlot,
    maxImagesCount,
    resetGarmentMeasurementService,
    openTourGuideModal,
  } = useGarmentMeasurementServiceContext();

  const multipleImagesInputRef = useRef<HTMLInputElement>(null);
  const singleImageInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0], index);
    }
  };

  const handleMultipleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleMultipleImagesUpload(files);

      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleRemoveAll = () => {
    resetGarmentMeasurementService();

    if (multipleImagesInputRef.current) {
      multipleImagesInputRef.current.value = "";
    }

    singleImageInputRefs.current.forEach((input) => {
      if (input) {
        input.value = "";
      }
    });
  };

  const validImagesCount = uploadedImages.filter((img) => img !== "").length;

  return (
    <div className="flex flex-col items-center w-full gap-8 md:gap-16">
      <div className="flex flex-col w-full max-w-2xl gap-4 p-4 border rounded-lg border-gorgonzola-blue-50">
        <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
          <div className="flex items-center gap-2 font-bold text-center md:text-left text-gorgonzola-blue-300">
            <h2>Make sure to follow these instructions when uploading your garment images</h2>
          </div>
          <MainButton onClick={openTourGuideModal} type="secondary" customClass="min-w-fit">
            <InfoCircle size={16} variant="Broken" className="text-gorgonzola-blue-400" />
            <span>View Guide</span>
          </MainButton>
        </div>
        <ul className="space-y-2 text-xs list-disc ps-6 text-closed-shutter-300">
          <li>Ensure the garment is a T-shirt.</li>
          <li>Capture the entire front of the T-shirt in portrait orientation (no tilted or rotated images).</li>
          <li>Place a flat A4 sheet next to the T-shirt for scale (not on top) and ensure it has no folds or wrinkles.</li>
          <li>Make sure all corners of both the T-shirt and the A4 sheet are fully visible.</li>
          <li>Use a flat surface with a plain, uncluttered background.</li>
          <li>Avoid shadows for accurate measurements.</li>
          <li>Keep each image under 3MB.</li>
          <li>You can upload up to {maxImagesCount} images.</li>
        </ul>
      </div>

      <div className="flex flex-col items-center justify-center w-full gap-4 md:flex-row">
        <MainButton type="secondary">
          <label htmlFor="multipleGarmentImages" className="flex items-center justify-center h-full min-w-full gap-2 py-2 cursor-pointer">
            <ImportCurve variant="Broken" />
            <span>Upload Multiple Images</span>
          </label>
        </MainButton>
        <input id="multipleGarmentImages" type="file" className="hidden" onChange={handleMultipleImagesChange} accept="image/*" multiple ref={multipleImagesInputRef} />

        <MainButton type="error" onClick={handleRemoveAll} disabled={validImagesCount === 0}>
          <div className="flex items-center gap-2">
            <Trash variant="Broken" />
            <span>Remove All</span>
          </div>
        </MainButton>
      </div>

      <div className="grid w-full max-w-5xl grid-cols-1 gap-6 md:grid-cols-3 sm:grid-cols-2 lg:grid-cols-4">
        {uploadedImages.map((image, index) => (
          <div key={index} className="flex flex-col gap-2">
            <div className="relative">
              <div className="grid place-content-center w-full h-[15rem] border-2 rounded-lg border-gorgonzola-blue-50 overflow-hidden relative">
                {image ? (
                  <img src={`data:image/png;base64,${processImageForDisplay(image)}`} alt={`Uploaded garment ${index + 1}`} className="object-contain w-full h-full" />
                ) : (
                  <div className="grid w-full place-content-center">
                    <img src="/assets/image/free-trial/garment-measurement/default-garment.png" alt="Default garment" className="object-contain w-[15rem] h-[20rem]" />
                  </div>
                )}
              </div>

              {uploadedImages.length > 1 && (
                <button
                  onClick={() => removeImage(index)}
                  className="absolute z-20 p-2 transition-colors bg-white border rounded-full shadow-sm top-2 right-2 border-gorgonzola-blue-50 hover:bg-gorgonzola-blue-50/30"
                  aria-label="Remove image"
                >
                  <Trash size={18} variant="Broken" className="text-english-vermillion-300" />
                </button>
              )}
            </div>

            <div className="flex flex-col w-full gap-2">
              <MainButton type="primary" customClass="justify-center !p-0">
                <label htmlFor={`garmentImage-${index}`} className="flex items-center justify-center h-full min-w-full gap-2 py-2 cursor-pointer">
                  {image ? <CloudChange variant="Broken" /> : <DocumentUpload variant="Broken" />}
                  <span>{image ? "Change Image" : `Upload Image ${index + 1}`}</span>
                </label>
              </MainButton>

              <input
                id={`garmentImage-${index}`}
                type="file"
                className="hidden"
                onChange={(e) => handleImageChange(e, index)}
                accept="image/*"
                ref={(el) => (singleImageInputRefs.current[index] = el)}
              />

              {imageErrors[index] && <UploadErrorMessage message={imageErrors[index]} />}
            </div>
          </div>
        ))}

        {uploadedImages.length < maxImagesCount && (
          <div
            className="flex flex-col items-center justify-center w-full h-[15rem] transition-colors border-2 border-dashed rounded-lg cursor-pointer border-gorgonzola-blue-100 hover:bg-gorgonzola-blue-25"
            onClick={addNewImageSlot}
          >
            <Add size={40} variant="Broken" className="mb-2 text-gorgonzola-blue-300" />
            <p className="text-xs font-medium text-gorgonzola-blue-300">Add More Images</p>
            <p className="mt-1 text-xs text-gorgonzola-blue-200">
              ({uploadedImages.length}/{maxImagesCount})
            </p>
          </div>
        )}
      </div>

      <GarmentMeasurementServicePagination />
    </div>
  );
};

export default ImageUpload;
